/**
 * missionStore — Zustand store du module Missions.
 *
 * Pattern : suit instagramStore.ts en plus simple (pas de cache LRU complexe en V1).
 * Cache léger 30s sur les missions list pour éviter les re-fetch en navigation.
 */

import { create } from 'zustand';
import api from '../utils/api';
import { handleApiError, handleApiSuccess } from '../utils/errorHandler';
import { logger } from '../utils/logger';
import type {
  Mission,
  MissionStep,
  MissionMedia,
  MissionInvoice,
  MissionResource,
  MissionNote,
  MissionTemplate,
  MissionDashboard,
  StepStatus,
  MissionStatus,
} from '../types/missions';

const CACHE_TTL_MS = 30_000;

// Grâce rail (3h) : mémorise localement les clients dont on vient d'enlever une tâche
// (le hard delete efface la mission → le backend n'a plus de trace ; l'archivage aussi couvert).
export const touchClientGrace = (clientId?: string | null) => {
  if (!clientId) return;
  try {
    const raw = JSON.parse(localStorage.getItem('nudge-client-grace') || '{}');
    raw[clientId] = Date.now();
    // purge des entrées > 4h
    for (const k of Object.keys(raw)) if (Date.now() - raw[k] > 4 * 3600_000) delete raw[k];
    localStorage.setItem('nudge-client-grace', JSON.stringify(raw));
  } catch { /* localStorage indisponible : tant pis */ }
};

interface MissionStore {
  missions: Mission[];
  currentMission: Mission | null;
  templates: MissionTemplate[];
  dashboard: MissionDashboard | null;
  isLoading: boolean;
  isLoadingDetail: boolean;
  loadError: boolean;

  _missionsFetchedAt: number | null;
  _dashboardFetchedAt: number | null;

  // Actions — missions
  fetchMissions: (params?: {
    clientId?: string;
    status?: string;
    type?: string;
    archived?: boolean;
    search?: string;
  }) => Promise<void>;
  fetchMissionById: (id: string) => Promise<Mission | null>;
  createMission: (data: Partial<Mission> & { clientId: string; title: string }) => Promise<Mission>;
  updateMission: (id: string, data: Partial<Mission>) => Promise<Mission>;
  archiveMission: (id: string) => Promise<void>;
  deleteMission: (id: string) => Promise<void>;
  unarchiveMission: (id: string) => Promise<void>;
  duplicateMission: (id: string) => Promise<Mission>;
  setCurrentMission: (mission: Mission | null) => void;

  // Actions — steps
  addStep: (missionId: string, step: Partial<MissionStep> & { title: string }) => Promise<MissionStep>;
  updateStep: (stepId: string, fields: Partial<MissionStep>) => Promise<MissionStep>;
  deleteStep: (stepId: string) => Promise<void>;
  reorderSteps: (missionId: string, stepIds: string[]) => Promise<void>;
  setStepStatus: (stepId: string, status: StepStatus, awaitingContact?: string) => Promise<MissionStep>;

  // Actions — notes
  addNote: (missionId: string, contentMd: string, opts?: { title?: string; pinned?: boolean; stepId?: string }) => Promise<MissionNote>;
  updateNote: (noteId: string, fields: Partial<MissionNote>) => Promise<MissionNote>;
  deleteNote: (noteId: string) => Promise<void>;

  // Actions — resources
  addResource: (missionId: string, data: Partial<MissionResource> & { kind: string; label: string }) => Promise<MissionResource>;
  updateResource: (resourceId: string, fields: Partial<MissionResource>) => Promise<MissionResource>;
  deleteResource: (resourceId: string) => Promise<void>;
  touchResource: (resourceId: string) => Promise<void>;

  // Actions — media
  uploadMedia: (missionId: string, file: File, opts?: { kind?: string; stepId?: string; label?: string }) => Promise<MissionMedia>;
  addMediaLink: (missionId: string, data: { kind: string; storage: string; storageUrl: string; label?: string; stepId?: string }) => Promise<MissionMedia>;
  uploadMediaVersion: (missionId: string, mediaId: string, file: File) => Promise<MissionMedia>;
  updateMedia: (missionId: string, mediaId: string, fields: Partial<MissionMedia>) => Promise<MissionMedia>;
  shareMedia: (missionId: string, mediaId: string, opts?: { expiresAt?: string; allowChangesRequest?: boolean }) => Promise<{ shareUrl: string; shareToken: string; expiresAt?: string }>;
  archiveMedia: (missionId: string, mediaId: string) => Promise<void>;

  // Actions — invoices
  addInvoice: (missionId: string, data: Partial<MissionInvoice> & { kind: string; amountTtcCents: number; createInQonto?: boolean }) => Promise<MissionInvoice>;
  updateInvoice: (missionId: string, invoiceId: string, fields: Partial<MissionInvoice>) => Promise<MissionInvoice>;
  deleteInvoice: (missionId: string, invoiceId: string) => Promise<void>;
  syncInvoiceFromQonto: (missionId: string, invoiceId: string) => Promise<MissionInvoice>;

  // Actions — comments
  addComment: (missionId: string, text: string, stepId?: string) => Promise<void>;

  // Actions — templates
  fetchTemplates: () => Promise<void>;

  // Actions — dashboard
  fetchDashboard: () => Promise<void>;
}

export const useMissionStore = create<MissionStore>((set, get) => ({
  missions: [],
  currentMission: null,
  templates: [],
  dashboard: null,
  isLoading: false,
  isLoadingDetail: false,
  loadError: false,
  _missionsFetchedAt: null,
  _dashboardFetchedAt: null,

  // ============================================================
  // MISSIONS
  // ============================================================

  fetchMissions: async (params) => {
    const cached = get()._missionsFetchedAt;
    const fresh = cached && Date.now() - cached < CACHE_TTL_MS;
    // Cache hit : si récent ET pas de filtre spécifique, on évite l'appel réseau
    // (neutralise les rafales de refetch déclenchées par les events temps-réel de sous-entités).
    if (fresh && !params && get().missions.length) return;
    if (!fresh) set({ isLoading: !get().missions.length, loadError: false });
    try {
      // limit: 200 par défaut (plafond backend) : sans lui, l'API tronque à 50 et
      // des missions actives disparaissent de l'interface dès qu'on en a plus de 50.
      const response = await api.get('/missions', { params: { limit: 200, ...params } });
      const items = response.data?.items || [];
      set({ missions: items, isLoading: false, loadError: false, _missionsFetchedAt: Date.now() });
      logger.log('✅ Missions chargées:', items.length);
    } catch (error: any) {
      handleApiError(error, 'le chargement des missions');
      set({ isLoading: false, loadError: true });
      throw error;
    }
  },

  fetchMissionById: async (id) => {
    set({ isLoadingDetail: true });
    try {
      const response = await api.get(`/missions/${id}`);
      const mission = response.data;
      set((state) => ({
        currentMission: mission,
        isLoadingDetail: false,
        missions: state.missions.map((m) => (m.id === id ? { ...m, ...mission } : m)),
      }));
      return mission;
    } catch (error: any) {
      handleApiError(error, 'la récupération de la mission');
      set({ isLoadingDetail: false });
      return null;
    }
  },

  createMission: async (data) => {
    try {
      const response = await api.post('/missions', data);
      const mission = response.data.mission;
      set((state) => ({ missions: [mission, ...state.missions], _missionsFetchedAt: null }));
      handleApiSuccess('Mission créée');
      return mission;
    } catch (error: any) {
      handleApiError(error, 'la création de la mission');
      throw error;
    }
  },

  updateMission: async (id, data) => {
    // Optimistic : applique immédiatement le patch local pour éviter le clignotement
    // (drag/resize : sinon le state local du composant resync sur d'anciennes coords le temps du round-trip).
    set((state) => ({
      missions: state.missions.map((m) => (m.id === id ? { ...m, ...data } : m)),
      currentMission: state.currentMission?.id === id ? { ...state.currentMission, ...data } : state.currentMission,
    }));
    try {
      const response = await api.patch(`/missions/${id}`, data);
      const mission = response.data;
      set((state) => ({
        missions: state.missions.map((m) => (m.id === id ? { ...m, ...mission } : m)),
        currentMission: state.currentMission?.id === id ? { ...state.currentMission, ...mission } : state.currentMission,
      }));
      return mission;
    } catch (error: any) {
      handleApiError(error, 'la mise à jour de la mission');
      throw error;
    }
  },

  archiveMission: async (id) => {
    try {
      touchClientGrace(get().missions.find((m) => m.id === id)?.clientId);
      await api.post(`/missions/${id}/archive`);
      set((state) => ({ missions: state.missions.filter((m) => m.id !== id) }));
      handleApiSuccess('Mission archivée');
    } catch (error: any) {
      handleApiError(error, "l'archivage");
      throw error;
    }
  },

  deleteMission: async (id) => {
    try {
      touchClientGrace(get().missions.find((m) => m.id === id)?.clientId);
      await api.delete(`/missions/${id}`);
      set((state) => ({ missions: state.missions.filter((m) => m.id !== id) }));
      handleApiSuccess('Tâche supprimée');
    } catch (error: any) {
      handleApiError(error, 'la suppression');
      throw error;
    }
  },

  unarchiveMission: async (id) => {
    try {
      await api.post(`/missions/${id}/unarchive`);
      set({ _missionsFetchedAt: null });
      await get().fetchMissions();
    } catch (error: any) {
      handleApiError(error, 'la restauration');
      throw error;
    }
  },

  duplicateMission: async (id) => {
    try {
      const response = await api.post(`/missions/${id}/duplicate`);
      const mission = response.data.mission;
      set((state) => ({ missions: [mission, ...state.missions] }));
      handleApiSuccess('Mission dupliquée');
      return mission;
    } catch (error: any) {
      handleApiError(error, 'la duplication');
      throw error;
    }
  },

  setCurrentMission: (mission) => set({ currentMission: mission }),

  // ============================================================
  // STEPS
  // ============================================================

  addStep: async (missionId, step) => {
    const response = await api.post(`/missions/${missionId}/steps`, step);
    const created = response.data.step;
    // MAJ atomique : currentMission ET missions[] (utilisé par le board view).
    set((state) => ({
      currentMission: state.currentMission?.id === missionId
        ? { ...state.currentMission, steps: [...(state.currentMission.steps || []), created] }
        : state.currentMission,
      missions: state.missions.map(m => m.id === missionId
        ? { ...m, steps: [...(m.steps || []), created] }
        : m),
    }));
    return created;
  },

  updateStep: async (stepId, fields) => {
    // OPTIMISTE (08/07) : on applique localement AVANT l'appel API → le toggle d'étape
    // répond instantanément (zéro lag perçu). Rollback sur l'état précédent si l'API échoue.
    const prevMissions = get().missions;
    const prevCurrent = get().currentMission;
    const patch = (steps?: MissionStep[]) => steps?.map((s) => (s.id === stepId ? { ...s, ...fields } : s));
    set((state) => ({
      currentMission: state.currentMission
        ? { ...state.currentMission, steps: patch(state.currentMission.steps) }
        : state.currentMission,
      missions: state.missions.map(m => (m.steps?.some(s => s.id === stepId) ? { ...m, steps: patch(m.steps) } : m)),
    }));
    try {
      const response = await api.patch(`/mission-steps/${stepId}`, fields);
      const step = response.data;
      // Consolider avec la vérité serveur (doneAt, awaitingSince…)
      const settle = (steps?: MissionStep[]) => steps?.map((s) => (s.id === stepId ? step : s));
      set((state) => ({
        currentMission: state.currentMission
          ? { ...state.currentMission, steps: settle(state.currentMission.steps) }
          : state.currentMission,
        missions: state.missions.map(m => (m.steps?.some(s => s.id === stepId) ? { ...m, steps: settle(m.steps) } : m)),
      }));
      return step;
    } catch (e) {
      set({ missions: prevMissions, currentMission: prevCurrent }); // rollback
      throw e;
    }
  },

  deleteStep: async (stepId) => {
    await api.delete(`/mission-steps/${stepId}`);
    set((state) => ({
      currentMission: state.currentMission ? {
        ...state.currentMission,
        steps: state.currentMission.steps?.filter((s) => s.id !== stepId),
      } : state.currentMission,
      missions: state.missions.map(m => ({
        ...m,
        steps: m.steps?.filter(s => s.id !== stepId),
      })),
    }));
  },

  reorderSteps: async (missionId, stepIds) => {
    await api.post(`/missions/${missionId}/steps/reorder`, { stepIds });
    set((state) => state.currentMission?.id === missionId ? {
      currentMission: {
        ...state.currentMission,
        steps: stepIds
          .map((id, idx) => state.currentMission!.steps?.find((s) => s.id === id) && { ...state.currentMission!.steps!.find((s) => s.id === id)!, position: idx })
          .filter(Boolean) as MissionStep[],
      },
    } : {});
  },

  setStepStatus: async (stepId, status, awaitingContact) => {
    return get().updateStep(stepId, { status, awaitingContact });
  },

  // ============================================================
  // NOTES
  // ============================================================

  addNote: async (missionId, contentMd, opts) => {
    const response = await api.post(`/missions/${missionId}/notes`, { contentMd, ...opts });
    const note = response.data.note;
    set((state) => state.currentMission?.id === missionId ? {
      currentMission: { ...state.currentMission, notes: [note, ...(state.currentMission.notes || [])] },
    } : {});
    return note;
  },

  updateNote: async (noteId, fields) => {
    const response = await api.patch(`/mission-notes/${noteId}`, fields);
    const note = response.data;
    set((state) => state.currentMission ? {
      currentMission: {
        ...state.currentMission,
        notes: state.currentMission.notes?.map((n) => (n.id === noteId ? note : n)),
      },
    } : {});
    return note;
  },

  deleteNote: async (noteId) => {
    await api.delete(`/mission-notes/${noteId}`);
    set((state) => state.currentMission ? {
      currentMission: {
        ...state.currentMission,
        notes: state.currentMission.notes?.filter((n) => n.id !== noteId),
      },
    } : {});
  },

  // ============================================================
  // RESOURCES
  // ============================================================

  addResource: async (missionId, data) => {
    const response = await api.post(`/missions/${missionId}/resources`, data);
    const resource = response.data.resource;
    set((state) => state.currentMission?.id === missionId ? {
      currentMission: { ...state.currentMission, resources: [...(state.currentMission.resources || []), resource] },
    } : {});
    return resource;
  },

  updateResource: async (resourceId, fields) => {
    const response = await api.patch(`/mission-resources/${resourceId}`, fields);
    const resource = response.data;
    set((state) => state.currentMission ? {
      currentMission: {
        ...state.currentMission,
        resources: state.currentMission.resources?.map((r) => (r.id === resourceId ? resource : r)),
      },
    } : {});
    return resource;
  },

  deleteResource: async (resourceId) => {
    await api.delete(`/mission-resources/${resourceId}`);
    set((state) => state.currentMission ? {
      currentMission: {
        ...state.currentMission,
        resources: state.currentMission.resources?.filter((r) => r.id !== resourceId),
      },
    } : {});
  },

  touchResource: async (resourceId) => {
    try { await api.post(`/mission-resources/${resourceId}/touch`); } catch {}
  },

  // ============================================================
  // MEDIA
  // ============================================================

  uploadMedia: async (missionId, file, opts) => {
    const formData = new FormData();
    formData.append('file', file);
    if (opts?.kind) formData.append('kind', opts.kind);
    if (opts?.stepId) formData.append('stepId', opts.stepId);
    if (opts?.label) formData.append('label', opts.label);
    const response = await api.post(`/missions/${missionId}/media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const media = response.data.media;
    set((state) => state.currentMission?.id === missionId ? {
      currentMission: { ...state.currentMission, media: [...(state.currentMission.media || []), media] },
    } : {});
    handleApiSuccess('Média uploadé');
    return media;
  },

  addMediaLink: async (missionId, data) => {
    const response = await api.post(`/missions/${missionId}/media`, data);
    const media = response.data.media;
    set((state) => state.currentMission?.id === missionId ? {
      currentMission: { ...state.currentMission, media: [...(state.currentMission.media || []), media] },
    } : {});
    handleApiSuccess('Lien ajouté');
    return media;
  },

  uploadMediaVersion: async (missionId, mediaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/missions/${missionId}/media/${mediaId}/version`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const media = response.data.media;
    // Recharge la mission pour avoir l'historique des versions à jour
    if (get().currentMission?.id === missionId) await get().fetchMissionById(missionId);
    handleApiSuccess('Nouvelle version uploadée');
    return media;
  },

  updateMedia: async (missionId, mediaId, fields) => {
    const response = await api.patch(`/missions/${missionId}/media/${mediaId}`, fields);
    const media = response.data;
    set((state) => state.currentMission?.id === missionId ? {
      currentMission: {
        ...state.currentMission,
        media: state.currentMission.media?.map((m) => (m.id === mediaId ? media : m)),
      },
    } : {});
    return media;
  },

  shareMedia: async (missionId, mediaId, opts) => {
    const response = await api.post(`/missions/${missionId}/media/${mediaId}/share`, opts || {});
    if (get().currentMission?.id === missionId) await get().fetchMissionById(missionId);
    return response.data;
  },

  archiveMedia: async (missionId, mediaId) => {
    await api.delete(`/missions/${missionId}/media/${mediaId}`);
    set((state) => state.currentMission?.id === missionId ? {
      currentMission: {
        ...state.currentMission,
        media: state.currentMission.media?.filter((m) => m.id !== mediaId),
      },
    } : {});
  },

  // ============================================================
  // INVOICES
  // ============================================================

  addInvoice: async (missionId, data) => {
    const response = await api.post(`/missions/${missionId}/invoices`, data);
    const invoice = response.data.invoice;
    if (get().currentMission?.id === missionId) await get().fetchMissionById(missionId);
    handleApiSuccess(response.data.message || 'Document créé');
    return invoice;
  },

  updateInvoice: async (missionId, invoiceId, fields) => {
    const response = await api.patch(`/missions/${missionId}/invoices/${invoiceId}`, fields);
    const invoice = response.data;
    if (get().currentMission?.id === missionId) await get().fetchMissionById(missionId);
    return invoice;
  },

  deleteInvoice: async (missionId, invoiceId) => {
    await api.delete(`/missions/${missionId}/invoices/${invoiceId}`);
    if (get().currentMission?.id === missionId) await get().fetchMissionById(missionId);
  },

  syncInvoiceFromQonto: async (missionId, invoiceId) => {
    const response = await api.post(`/missions/${missionId}/invoices/${invoiceId}/sync-qonto`);
    if (get().currentMission?.id === missionId) await get().fetchMissionById(missionId);
    handleApiSuccess('Sync Qonto OK');
    return response.data.invoice;
  },

  // ============================================================
  // COMMENTS
  // ============================================================

  addComment: async (missionId, text, stepId) => {
    await api.post(`/missions/${missionId}/comments`, { text, stepId });
    if (get().currentMission?.id === missionId) await get().fetchMissionById(missionId);
  },

  // ============================================================
  // TEMPLATES
  // ============================================================

  fetchTemplates: async () => {
    if (get().templates.length) return;
    try {
      const response = await api.get('/mission-templates');
      set({ templates: response.data });
    } catch (error: any) {
      handleApiError(error, 'le chargement des templates');
    }
  },

  // ============================================================
  // DASHBOARD
  // ============================================================

  fetchDashboard: async () => {
    const cached = get()._dashboardFetchedAt;
    if (cached && Date.now() - cached < CACHE_TTL_MS) return;
    try {
      const response = await api.get('/missions/dashboard/summary');
      set({ dashboard: response.data, _dashboardFetchedAt: Date.now() });
    } catch (error: any) {
      handleApiError(error, 'le chargement du dashboard missions');
    }
  },
}));
